const RELEASED_FIELDS = Object.freeze(["caughtIds", "favouriteIds", "showDried"]);
const V2_FIELDS = Object.freeze([
  "version", ...RELEASED_FIELDS, "catchCounts", "obtainedTitleIds", "showContestTitles",
]);
const REQUIRED_FIELDS = Object.freeze(V2_FIELDS.filter((field) => field !== "caughtIds"));
const LEGACY_ALLOWED_FIELDS = new Set([...RELEASED_FIELDS, "show_dried"]);
const JSON_TEXT_ENCODER = new TextEncoder();

export const FISHYDEX_PRESET_PAYLOAD_VERSION = 3;
export const MAX_FISHYDEX_PRESET_IDS_PER_LIST_V1 = 4_096;
export const MAX_FISHYDEX_PRESET_PAYLOAD_BYTES_V1 = 64 * 1_024;
export const MAX_FISHYDEX_PRESET_PAYLOAD_BYTES_V2 = 128 * 1_024;
// A V2 snapshot can contain disjoint full caught-ID and count lists. Migration
// must retain both, and count records occupy more bytes than their former IDs.
export const MAX_FISHYDEX_PRESET_CATCH_COUNTS_V3 = 8_192;
export const MAX_FISHYDEX_PRESET_PAYLOAD_BYTES_V3 = 512 * 1_024;

function isPlainRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === null || prototype === Object.prototype;
}

function requireExactFields(value, fields, label = "Fishydex preset payload") {
  if (!isPlainRecord(value)) throw new TypeError(`${label} must be an object`);
  for (const field of fields) {
    if (!Object.prototype.hasOwnProperty.call(value, field)) {
      throw new TypeError(`${label} is missing ${field}`);
    }
  }
  const unsupported = Object.keys(value).find((field) => !fields.includes(field));
  if (unsupported !== undefined) throw new TypeError(`${label} has unsupported field ${unsupported}`);
}

function positiveId(value, label) {
  if (!Number.isInteger(value) || value < 1 || value > 4_294_967_295) {
    throw new TypeError(`${label} must be a positive u32`);
  }
  return value;
}

function requireList(values, field, limit = MAX_FISHYDEX_PRESET_IDS_PER_LIST_V1) {
  if (!Array.isArray(values)) throw new TypeError(`Fishydex preset ${field} must be an array`);
  if (values.length > limit) {
    throw new TypeError(`Fishydex preset ${field} exceeds its item limit`);
  }
}

function canonicalIds(values, field) {
  requireList(values, field);
  const ids = values.map((value, index) => positiveId(value, `Fishydex preset ${field}[${index}]`))
    .sort((left, right) => left - right);
  return ids.filter((value, index) => index === 0 || value !== ids[index - 1]);
}

function canonicalCatchCounts(values, limit = MAX_FISHYDEX_PRESET_CATCH_COUNTS_V3) {
  requireList(values, "catchCounts", limit);
  const seen = new Set();
  return values.map((value) => {
    requireExactFields(value, ["itemId", "count", "source"], "Fishydex catch count");
    const itemId = positiveId(value.itemId, "Fishydex catch count itemId");
    if (seen.has(itemId)) throw new TypeError("Fishydex catchCounts has a duplicate itemId");
    seen.add(itemId);
    if (!Number.isInteger(value.count) || value.count < 0 || value.count > 4_294_967_295) {
      throw new TypeError("Fishydex catch count must be a u32");
    }
    if (value.source !== "manual" && value.source !== "title") {
      throw new TypeError("Fishydex catch count source must be manual or title");
    }
    return { itemId, count: value.count, source: value.source };
  }).sort((left, right) => left.itemId - right.itemId);
}

export function defaultFishydexPresetPayload() {
  return {
    version: FISHYDEX_PRESET_PAYLOAD_VERSION,
    favouriteIds: [], showDried: false,
    catchCounts: [], obtainedTitleIds: [], showContestTitles: false,
  };
}

export function decodeFishydexPresetPayload(value) {
  // Only exact released schemas migrate; partial/future snapshots are rejected.
  const released = isPlainRecord(value) && !Object.prototype.hasOwnProperty.call(value, "version");
  const v2 = !released && value?.version === 2;
  const hasUnobtainable = !released && !v2 && Object.hasOwn(value ?? {}, "showUnobtainableTitles");
  requireExactFields(value, released ? RELEASED_FIELDS : v2 ? V2_FIELDS
    : hasUnobtainable ? [...REQUIRED_FIELDS, "showUnobtainableTitles"] : REQUIRED_FIELDS);
  if (hasUnobtainable && typeof value.showUnobtainableTitles !== "boolean") {
    throw new TypeError("Fishydex preset showUnobtainableTitles must be a boolean");
  }
  if (!released && !v2 && value.version !== FISHYDEX_PRESET_PAYLOAD_VERSION) {
    throw new TypeError("Fishydex preset payload version must be 2 or 3");
  }
  if (typeof value.showDried !== "boolean") {
    throw new TypeError("Fishydex preset showDried must be a boolean");
  }
  if (!released && typeof value.showContestTitles !== "boolean") {
    throw new TypeError("Fishydex preset showContestTitles must be a boolean");
  }
  const caughtIds = released || v2 ? canonicalIds(value.caughtIds, "caughtIds") : [];
  const payload = {
    version: FISHYDEX_PRESET_PAYLOAD_VERSION,
    favouriteIds: canonicalIds(value.favouriteIds, "favouriteIds"),
    showDried: value.showDried,
    catchCounts: released ? [] : canonicalCatchCounts(value.catchCounts,
      v2 ? MAX_FISHYDEX_PRESET_IDS_PER_LIST_V1 : MAX_FISHYDEX_PRESET_CATCH_COUNTS_V3),
    obtainedTitleIds: released ? [] : canonicalIds(value.obtainedTitleIds, "obtainedTitleIds"),
    showContestTitles: released ? false : value.showContestTitles,
    // Optional opt-in: omission is the canonical encoding of disabled.
    ...(hasUnobtainable && value.showUnobtainableTitles ? { showUnobtainableTitles: true } : {}),
  };
  const boundedPayload = released
    ? { caughtIds, favouriteIds: payload.favouriteIds, showDried: payload.showDried }
    : v2 ? { ...payload, version: 2, caughtIds } : payload;
  const limit = released ? MAX_FISHYDEX_PRESET_PAYLOAD_BYTES_V1
    : v2 ? MAX_FISHYDEX_PRESET_PAYLOAD_BYTES_V2 : MAX_FISHYDEX_PRESET_PAYLOAD_BYTES_V3;
  if (JSON_TEXT_ENCODER.encode(JSON.stringify(boundedPayload)).byteLength > limit) {
    throw new TypeError("Fishydex preset payload exceeds its byte limit");
  }
  if (caughtIds.length > 0) {
    const counts = new Map(payload.catchCounts.map((entry) => [entry.itemId, entry]));
    for (const itemId of caughtIds) {
      const existing = counts.get(itemId);
      if (!existing || existing.count === 0) counts.set(itemId, { itemId, count: 1, source: "manual" });
    }
    payload.catchCounts = Array.from(counts.values()).sort((left, right) => left.itemId - right.itemId);
  }
  return payload;
}

export function migrateLegacyFishydexPresetPayload(value) {
  if (isPlainRecord(value) && Object.prototype.hasOwnProperty.call(value, "version")) {
    return decodeFishydexPresetPayload(value);
  }
  if (!isPlainRecord(value)) throw new TypeError("Legacy Fishydex preset payload must be an object");
  const unsupported = Object.keys(value).find((field) => !LEGACY_ALLOWED_FIELDS.has(field));
  if (unsupported !== undefined) {
    throw new TypeError(`Legacy Fishydex preset payload has unsupported field ${unsupported}`);
  }
  if (!Object.prototype.hasOwnProperty.call(value, "caughtIds")
      || !Object.prototype.hasOwnProperty.call(value, "favouriteIds")) {
    throw new TypeError("Legacy Fishydex preset payload is missing fish IDs");
  }
  if (Object.prototype.hasOwnProperty.call(value, "showDried")
      && Object.prototype.hasOwnProperty.call(value, "show_dried")) {
    throw new TypeError("Legacy Fishydex preset payload has conflicting dried-fish fields");
  }
  const driedValue = Object.prototype.hasOwnProperty.call(value, "showDried")
    ? value.showDried
    : Object.prototype.hasOwnProperty.call(value, "show_dried") ? value.show_dried : false;
  let showDried;
  if (driedValue === true || driedValue === "true" || driedValue === 1 || driedValue === "1") {
    showDried = true;
  } else if (driedValue === false || driedValue === "false" || driedValue === 0 || driedValue === "0") {
    showDried = false;
  } else {
    throw new TypeError("Legacy Fishydex preset showDried must be boolean-like");
  }
  return decodeFishydexPresetPayload({ caughtIds: value.caughtIds, favouriteIds: value.favouriteIds, showDried });
}

export function fishydexPresetPayloadsEqual(left, right) {
  return JSON.stringify(decodeFishydexPresetPayload(left)) === JSON.stringify(decodeFishydexPresetPayload(right));
}
